"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { PhoneCall, MapPin, Clock, ShieldCheck, CircleAlert as AlertCircle, Wrench, CircleCheck as CheckCircle2, Loader as Loader2, ChevronDown, ArrowRight, TriangleAlert as AlertTriangle, Search, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
const serviceNeededOptions = ["Plumbing Repair", "Water Heater", "Drain Cleaning", "Sewer Line", "Leak Detection", "Emergency Plumbing", "Other"];
const preferredTimeOptions = ["ASAP / Emergency", "Morning 8am-12pm", "Afternoon 12pm-4pm", "Evening 4pm-8pm"];
const emergencyWarningSigns = [{
  title: "Burst Pipe",
  desc: "Uncontrolled water spray or rapid ceiling / wall saturation."
}, {
  title: "Major Water Leak",
  desc: "Active water pooling near foundations, drywall, or fixtures."
}, {
  title: "Sewer Backup",
  desc: "Multiple drains gurgling, backing up, or sewage odor in living areas."
}, {
  title: "Overflowing Toilet",
  desc: "Toilet bowl continually rising or overflowing onto floors."
}, {
  title: "Water Heater Leak",
  desc: "Tank rupture, standing water around unit, or burner failure."
}, {
  title: "Active Flooding / Low Pressure",
  desc: "Sudden loss of house-wide water pressure with pooling."
}];
const diagnosticCards = [{
  title: "Water Leak",
  href: "/water-leak-problems/",
  desc: "Pinhole, slab, or pipe joint leaks"
}, {
  title: "Toilet Problem",
  href: "/toilet-problems/",
  desc: "Running, clogged, or leaking toilets"
}, {
  title: "Clogged Drain",
  href: "/drain-problems/",
  desc: "Slow kitchen, shower, or main drains"
}, {
  title: "Water Heater Problem",
  href: "/water-heater-problems/",
  desc: "No hot water, leaks, or rumbling tank"
}, {
  title: "Sewer Problem",
  href: "/drain-problems/",
  desc: "Main line blockages or sewer odors"
}, {
  title: "Low Water Pressure",
  href: "/water-pressure-problems/",
  desc: "Weak fixture stream across home"
}, {
  title: "Pipe Problem",
  href: "/pipe-problems/",
  desc: "Corrosion, banging, or frozen pipes"
}, {
  title: "Faucet / Sink Problem",
  href: "/fixture-problems/",
  desc: "Dripping taps, loose valves, or basin leaks"
}, {
  title: "Emergency",
  href: "/emergency-plumbing-problems/",
  desc: "Severe active water damage or stoppage"
}, {
  title: "Not Sure",
  href: "/problems/",
  desc: "Describe symptoms for on-site diagnostic"
}];
const serviceCards = [{
  title: "Emergency Plumbing",
  href: "/emergency-plumbing/",
  desc: "24/7 rapid dispatch for critical plumbing breakdowns"
}, {
  title: "Drain Cleaning",
  href: "/drain-cleaning/",
  desc: "Hydro jetting and cabling for stubborn line blockages"
}, {
  title: "Water Heater Repair",
  href: "/water-heater-repair/",
  desc: "Thermostats, heating elements, valves, and gas controls"
}, {
  title: "Water Heater Replacement",
  href: "/water-heater-replacement/",
  desc: "Tank and tankless modern unit installations"
}, {
  title: "Leak Detection & Repair",
  href: "/leak-detection-repair/",
  desc: "Non-invasive electronic acoustic leak location"
}, {
  title: "Toilet Repair",
  href: "/toilet-repair/",
  desc: "Flange replacement, wax seals, fill valves, and rebuilds"
}, {
  title: "Sewer Line Repair",
  href: "/sewer-line-repair/",
  desc: "Camera line inspections, trenchless and spot repairs"
}, {
  title: "Pipe Repair",
  href: "/pipe-repair/",
  desc: "Copper, PEX, CPVC pipe fixes and partial repiping"
}];
const coveredZones = ["Jonesboro", "Morrow", "Riverdale", "Forest Park", "Lovejoy", "Stockbridge"];
const faqItems = [{
  question: "How do I request emergency plumbing service in Jonesboro?",
  answer: "For immediate active flooding, burst pipes, or severe sewer backups, call (470) 847-9076 directly for live emergency dispatch. For non-urgent repairs or scheduled diagnostics, you can submit the online service request form on this page anytime."
}, {
  question: "What is the difference between calling vs using the online form?",
  answer: "Calling directly connects you immediately to our local dispatch team—recommended for active leaks, rising water, or nighttime emergencies. Submitting the form transmits your details directly to our desk, and a coordinator calls you back promptly to confirm arrival timing."
}, {
  question: "What information should I provide when requesting service?",
  answer: "Please provide your full name, best contact phone number, exact property address in Jonesboro or Clayton County, and a clear description of the plumbing symptom (such as leak location, fixture affected, or water shutoff status)."
}, {
  question: "Which service areas do you cover?",
  answer: "We are based locally in Jonesboro, GA, and provide full coverage across Clayton County and South Metro Atlanta, including Jonesboro, Morrow, Riverdale, Forest Park, Lovejoy, Stockbridge, and Ellenwood. View our full service areas page for complete neighborhood details.",
  linkText: "View All Service Areas",
  linkHref: "/service-areas/"
}, {
  question: "What types of plumbing problems do you handle?",
  answer: "Our licensed plumbers handle emergency burst pipes, water heater repair and replacement, whole-home repiping, sewer backups, drain clogs, toilet repairs, faucet leaks, and electronic leak detection. You can explore our dedicated problem diagnostic guides above."
}, {
  question: "What emergency safety steps should I take while waiting for the plumber?",
  answer: "If water is actively leaking, locate and shut off your property's main water valve or the isolation valve under the fixture. Turn off electrical power to the area if standing water touches outlets, and avoid running any other faucets or toilets until our technician inspects the line."
}, {
  question: "How do I identify which plumbing service I need?",
  answer: "If you are unsure of the root cause, browse our diagnostic cards above or choose 'Not Sure' on the form. Our technicians carry full diagnostic equipment on each service truck to inspect and isolate the issue on-site before recommending repairs."
}];
function RequestServiceFormComponent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card p-8 text-center shadow-md sm:p-12">
        <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-secondary text-primary">
          <CheckCircle2 className="size-8 text-primary" />
        </div>
        <h3 className="font-default text-2xl font-bold text-card-foreground">
          Service Request Received
        </h3>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          {successMessage}
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button type="button" onClick={resetForm} variant="outline" className="rounded-full px-6 py-2.5 font-semibold">
            Submit Another Request
          </Button>
          <Button asChild className="rounded-full bg-accent px-6 py-2.5 font-bold text-accent-foreground shadow-sm hover:bg-accent/90">
            <a href="tel:4708479076" className="flex items-center gap-2">
              <PhoneCall className="size-4" />
              <span>Call (470) 847-9076</span>
            </a>
          </Button>
        </div>
      </div>;
  }
  return <div className="rounded-2xl border border-border bg-card p-6 shadow-md sm:p-8 md:p-10">
      <div className="mb-6 border-b border-border pb-5">
        <div className="flex items-center gap-2 text-xs font-bold tracking-wider text-accent-foreground uppercase">
          <span className="flex size-2 rounded-full bg-accent" />
          Online Dispatch Desk
        </div>
        <h3 className="mt-1 font-default text-2xl font-bold text-card-foreground sm:text-3xl">
          Request Service
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Provide your property details and issue below. Our dispatch coordinator contacts you promptly to confirm arrival time.
        </p>
      </div>

      <Form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
        <FormField control={control} name="source_page" render={({
        field
      }) => <input type="hidden" {...field} />} />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField control={control} name="full_name" rules={{
          required: "Full name is required",
          minLength: {
            value: 2,
            message: "Please enter at least 2 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground text-sm font-semibold">
                  Full Name <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Your full name" className="h-11 rounded-lg border-input bg-background px-3.5 text-foreground shadow-xs focus-visible:border-primary focus-visible:ring-ring" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email address is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground text-sm font-semibold">
                  Email Address <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="you@example.com" className="h-11 rounded-lg border-input bg-background px-3.5 text-foreground shadow-xs focus-visible:border-primary focus-visible:ring-ring" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField control={control} name="phone" rules={{
          required: "Phone number is required",
          pattern: {
            value: /^[\d\s()+-]{7,20}$/,
            message: "Please enter a valid phone number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground text-sm font-semibold">
                  Phone Number <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(470) 000-0000" className="h-11 rounded-lg border-input bg-background px-3.5 text-foreground shadow-xs focus-visible:border-primary focus-visible:ring-ring" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="city" rules={{
          required: "City is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground text-sm font-semibold">
                  City <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Jonesboro" className="h-11 rounded-lg border-input bg-background px-3.5 text-foreground shadow-xs focus-visible:border-primary focus-visible:ring-ring" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField control={control} name="property_address" rules={{
          required: "Property address is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground text-sm font-semibold">
                  Property Address <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Street address (e.g. 123 Main St)" className="h-11 rounded-lg border-input bg-background px-3.5 text-foreground shadow-xs focus-visible:border-primary focus-visible:ring-ring" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="service_needed" rules={{
          required: "Please select a service needed"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground text-sm font-semibold">
                  Service Needed <span className="text-destructive">*</span>
                </FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-11 w-full rounded-lg border-input bg-background px-3.5 text-foreground shadow-xs focus-visible:border-primary focus-visible:ring-ring">
                      <SelectValue placeholder="Select service needed" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="border-border bg-popover text-popover-foreground">
                    {serviceNeededOptions.map((service, sIdx) => <SelectItem key={sIdx} value={service} className="cursor-pointer py-2">
                        {service}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="problem_issue" rules={{
        required: "Please describe your problem or issue"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-sm font-semibold">
                Problem / Issue Description <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Textarea rows={3} placeholder="Describe what is happening (e.g. burst pipe under kitchen sink, water heater leaking)..." className="rounded-lg border-input bg-background p-3.5 text-foreground shadow-xs focus-visible:border-primary focus-visible:ring-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_time" rules={{
        required: "Please select your preferred arrival time"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-sm font-semibold">
                Preferred Service Time <span className="text-destructive">*</span>
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                <FormControl>
                  <SelectTrigger className="h-11 w-full rounded-lg border-input bg-background px-3.5 text-foreground shadow-xs focus-visible:border-primary focus-visible:ring-ring">
                    <SelectValue placeholder="Select preferred arrival window" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="border-border bg-popover text-popover-foreground">
                  {preferredTimeOptions.map((timeOption, tIdx) => <SelectItem key={tIdx} value={timeOption} className="cursor-pointer py-2">
                      {timeOption}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="additional_details" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-sm font-semibold">
                Additional Details <span className="text-xs font-normal text-muted-foreground">(Optional)</span>
              </FormLabel>
              <FormControl>
                <Textarea rows={2} placeholder="Gate codes, parking instructions, or shutoff status..." className="rounded-lg border-input bg-background p-3.5 text-foreground shadow-xs focus-visible:border-primary focus-visible:ring-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="consent" rules={{
        required: "You must agree to be contacted before submitting",
        validate: value => value === true || "You must agree to be contacted before submitting"
      }} render={({
        field
      }) => <FormItem className="rounded-lg border border-border/80 bg-background/50 p-3.5 shadow-2xs">
              <div className="flex items-start gap-3">
                <FormControl>
                  <input type="checkbox" id="consent-checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} className="size-4.5 mt-0.5 rounded-sm border-input text-primary focus:ring-ring accent-primary cursor-pointer" />
                </FormControl>
                <FormLabel htmlFor="consent-checkbox" className="text-xs font-medium leading-relaxed text-foreground cursor-pointer sm:text-sm">
                  I agree to be contacted about my service request <span className="text-destructive">*</span>
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3.5 text-sm text-destructive">
            <AlertCircle className="size-5 shrink-0" />
            <p>{submitError}</p>
          </div>}

        <div className="pt-2">
          <Button type="submit" disabled={isSubmitting} className="h-12 w-full rounded-full bg-primary font-bold text-primary-foreground shadow-md transition-[background-color,box-shadow] hover:bg-primary/90">
            {isSubmitting ? <span className="flex items-center justify-center gap-2">
                <Loader2 className="size-5 animate-spin" />
                Submitting Request...
              </span> : <span className="flex items-center justify-center gap-2">
                <Wrench className="size-4" />
                Submit Service Request
              </span>}
          </Button>
          <p className="mt-2.5 text-center text-xs text-muted-foreground">
            Fast local dispatch. No credit card required to submit request.
          </p>
        </div>
      </Form>
    </div>;
}
export default function Contact() {
  const shouldReduceMotion = useReducedMotion();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const toggleFaq = (idx: number) => {
    setOpenFaq(prev => prev === idx ? null : idx);
  };
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        duration: 0.35
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <div className="bg-background text-foreground selection:bg-primary selection:text-primary-foreground">
      {/* ------------------------------------------------------------- */}
      {/* (1) HERO BLOCK */}
      {/* ------------------------------------------------------------- */}
      <section id="contact-hero" data-nav="light" className="relative border-b border-border bg-background py-14 md:py-20 lg:py-24">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
            {/* Left Content */}
            <motion.div variants={itemVariants} className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3.5 py-1.5 text-xs font-bold tracking-wide uppercase text-secondary-foreground">
                <span className="relative flex size-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                  <span className="relative inline-flex size-2.5 rounded-full bg-accent" />
                </span>
                24/7 Jonesboro Emergency Dispatch
              </div>

              <h1 className="mt-4 font-default text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-6xl">
                Contact Jonesboro Emergency Plumber
              </h1>

              <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                When plumbing failures strike your Jonesboro home or commercial facility, our local dispatch team responds immediately. Call our master line directly for rapid on-site emergency triage or submit a service request online.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="rounded-full bg-accent px-7 py-6 text-base font-bold text-accent-foreground shadow-md transition-[background-color,box-shadow] hover:bg-accent/90">
                  <a href="tel:4708479076" className="flex items-center gap-2.5">
                    <PhoneCall className="size-5 shrink-0" />
                    <span>Call (470) 847-9076</span>
                  </a>
                </Button>

                <Button asChild variant="outline" size="lg" className="rounded-full border-primary/20 bg-background px-7 py-6 text-base font-semibold text-primary shadow-xs transition-[background-color,border-color] hover:bg-secondary hover:text-primary">
                  <Link to="#request-service-form" className="flex items-center gap-2">
                    <span>Request Service</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-6 text-xs font-medium text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Clock className="size-4 text-primary" />
                  <span>24/7 Immediate Availability</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="size-4 text-primary" />
                  <span>Locally Stationed in Jonesboro, GA</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-4 text-primary" />
                  <span>Licensed &amp; Insured Plumbers</span>
                </div>
              </div>
            </motion.div>

            {/* Right Dispatch Card */}
            <motion.div variants={itemVariants} className="lg:col-span-5">
              <div className="rounded-2xl border border-border bg-card p-6 shadow-md sm:p-8">
                <div className="flex items-center justify-between border-b border-border pb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex size-11 items-center justify-center rounded-xl bg-secondary text-primary">
                      <Clock className="size-5 text-primary" />
                    </div>
                    <div>
                      <span className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Response Desk</span>
                      <h3 className="font-default text-lg font-bold text-foreground">Live Local Dispatch</h3>
                    </div>
                  </div>
                  <span className="inline-flex items-center rounded-md bg-accent/20 px-2.5 py-1 text-xs font-bold text-accent-foreground">
                    Active
                  </span>
                </div>

                <div className="mt-5 space-y-3.5 text-sm">
                  <div className="flex items-start justify-between border-b border-border/60 pb-3">
                    <span className="text-muted-foreground">Direct Line:</span>
                    <a href="tel:4708479076" className="font-bold text-primary underline underline-offset-2">
                      (470) 847-9076
                    </a>
                  </div>
                  <div className="flex items-start justify-between border-b border-border/60 pb-3">
                    <span className="text-muted-foreground">Coverage:</span>
                    <span className="font-medium text-foreground">Jonesboro &amp; Clayton County</span>
                  </div>
                  <div className="flex items-start justify-between border-b border-border/60 pb-3">
                    <span className="text-muted-foreground">Service Hours:</span>
                    <span className="font-medium text-foreground">24 Hours / 7 Days a Week</span>
                  </div>
                  <div className="flex items-start justify-between pb-1">
                    <span className="text-muted-foreground">Dispatch Status:</span>
                    <span className="font-semibold text-primary">Trucks Equipped for Rapid On-Site Repair</span>
                  </div>
                </div>

                <div className="mt-6 rounded-xl border-l-4 border-accent bg-muted/60 p-3.5 text-xs leading-relaxed text-muted-foreground">
                  <strong className="text-foreground">Direct Access:</strong> No call center hurdles. Speak with plumbing specialists prepared to assist with your specific situation.
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* (2) EMERGENCY CTA BANNER */}
      {/* ------------------------------------------------------------- */}
      <section id="emergency-banner" className="bg-primary py-14 text-primary-foreground md:py-18">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 px-3.5 py-1 text-xs font-bold tracking-wider uppercase text-accent">
              <AlertTriangle className="size-3.5" />
              Critical Warning Signs
            </div>
            <h2 className="mt-3 font-default text-2xl font-extrabold tracking-tight text-primary-foreground sm:text-3xl md:text-4xl">
              Have a Plumbing Emergency?
            </h2>
            <p className="mx-auto mt-2.5 max-w-2xl text-sm leading-relaxed text-primary-foreground/80 sm:text-base">
              If you are experiencing any of these high-risk signs, immediate shutoff and professional intervention are essential to avoid structural water damage.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {emergencyWarningSigns.map((item, i) => <div key={item.title} data-index={i} className="rounded-xl border border-primary-foreground/15 bg-primary-foreground/5 p-5 backdrop-blur-xs transition-[background-color,border-color] hover:bg-primary-foreground/10">
                <div className="flex items-center gap-3">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground font-bold text-sm">
                    {i + 1}
                  </div>
                  <h3 className="font-default text-base font-bold text-primary-foreground">
                    {item.title}
                  </h3>
                </div>
                <p className="mt-2.5 text-xs leading-relaxed text-primary-foreground/75">
                  {item.desc}
                </p>
              </div>)}
          </div>

          <div className="mt-10 text-center">
            <Button asChild size="lg" className="rounded-full bg-accent px-8 py-6 text-base font-bold text-accent-foreground shadow-lg transition-[background-color,box-shadow] hover:bg-accent/90">
              <a href="tel:4708479076" className="flex items-center justify-center gap-2.5">
                <PhoneCall className="size-5 shrink-0" />
                <span>CALL (470) 847-9076</span>
              </a>
            </Button>
            <p className="mt-3 text-xs text-primary-foreground/70">
              Live technicians available around the clock across Jonesboro, GA
            </p>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* (3) REQUEST SERVICE FORM */}
      {/* ------------------------------------------------------------- */}
      <section id="request-service-form" className="scroll-mt-10 border-b border-border bg-background py-14 md:py-20 lg:py-24">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
          <div className="mx-auto max-w-3xl">
            <div className="mb-8 text-center">
              <span className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
                Direct Scheduling
              </span>
              <h2 className="mt-2 font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl md:text-4xl">
                Submit a Service Request
              </h2>
              <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                Fill out the request form below. All entries route directly to our dispatch desk.
              </p>
            </div>

            <FormProvider formId="request_service_form" sectionName="contact-section" defaultValues={{
            source_page: "Contact Page - Primary Request Form",
            full_name: "",
            email: "",
            phone: "",
            property_address: "",
            city: "Jonesboro",
            service_needed: "",
            problem_issue: "",
            preferred_time: "",
            additional_details: "",
            consent: false
          }} validationRules={{
            source_page: {
              required: false
            },
            full_name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            property_address: {
              required: true
            },
            city: {
              required: true
            },
            service_needed: {
              required: true
            },
            problem_issue: {
              required: true
            },
            preferred_time: {
              required: true
            },
            additional_details: {
              required: false
            },
            consent: {
              required: true
            }
          }} submitText="Submit Service Request" successMessage="Thank you! Your service request has been transmitted to our Jonesboro emergency dispatch crew. A technician will call your phone shortly.">
              <RequestServiceFormComponent />
            </FormProvider>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* (4) PROBLEM FINDER */}
      {/* ------------------------------------------------------------- */}
      <section id="problem-finder" className="border-b border-border bg-card/50 py-14 md:py-20">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3.5 py-1 text-xs font-bold tracking-wide uppercase text-secondary-foreground">
              <Search className="size-3.5 text-primary" />
              Symptom Triage
            </div>
            <h2 className="mt-3 font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl md:text-4xl">
              What&apos;s Happening?
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground sm:text-base">
              Select your specific plumbing issue below to explore diagnostic guidance and rapid resolution options.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {diagnosticCards.map((item, i) => <Link key={item.title} to={item.href} data-index={i} className="group flex flex-col justify-between rounded-xl border border-border bg-card p-4.5 shadow-xs transition-[border-color,box-shadow,transform] hover:border-primary hover:bg-background hover:shadow-md">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="flex size-8 items-center justify-center rounded-lg bg-secondary text-primary font-bold text-xs group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      {i + 1}
                    </span>
                    <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                  </div>
                  <h3 className="mt-3 font-default text-base font-bold text-foreground group-hover:text-primary">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </div>
                <span className="mt-4 text-xs font-semibold text-primary inline-flex items-center gap-1">
                  Diagnose <ArrowRight className="size-3" />
                </span>
              </Link>)}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* (5) SERVICES GRID */}
      {/* ------------------------------------------------------------- */}
      <section id="services-grid" className="border-b border-border bg-background py-14 md:py-20">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
          <div className="text-center">
            <span className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
              Full Trade Capabilities
            </span>
            <h2 className="mt-2 font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl md:text-4xl">
              What Can We Help With?
            </h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">
              Comprehensive emergency repairs, diagnostic testing, and complete plumbing replacements throughout Jonesboro.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {serviceCards.map((srv, i) => <div key={srv.title} data-index={i} className="group flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-xs transition-[border-color,box-shadow] hover:border-primary/50 hover:bg-background hover:shadow-md">
                <div>
                  <div className="flex size-11 items-center justify-center rounded-xl bg-secondary text-primary">
                    <Wrench className="size-5 text-primary" />
                  </div>
                  <h3 className="mt-4 font-default text-lg font-bold text-foreground">
                    {srv.title}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                    {srv.desc}
                  </p>
                </div>

                <div className="mt-5 border-t border-border pt-4">
                  <Link to={srv.href} className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline">
                    <span>View Service Details</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* (6) SERVICE AREA */}
      {/* ------------------------------------------------------------- */}
      <section id="service-area" className="border-b border-border bg-card/40 py-14 md:py-20">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3.5 py-1 text-xs font-bold tracking-wide uppercase text-secondary-foreground">
                <MapPin className="size-3.5 text-primary" />
                Local Geographic Coverage
              </div>
              <h2 className="mt-3 font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl md:text-4xl">
                Plumbing Service in Jonesboro, GA
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                Jonesboro Emergency Plumber operates directly within Clayton County and South Metro Atlanta. Being positioned locally ensures our technicians arrive quickly with fully stocked service trucks equipped to handle emergency leaks, stoppages, and water heater breakdowns on the spot.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Whether you own a single-family home near Tara Boulevard or operate a commercial property along Highway 138, our team delivers transparent upfront estimates and code-compliant plumbing solutions.
              </p>

              <div className="mt-6">
                <Button asChild variant="outline" className="rounded-full border-primary/30 px-6 py-2.5 text-xs font-bold text-primary shadow-xs hover:bg-secondary hover:text-primary">
                  <Link to="/service-areas/" className="inline-flex items-center gap-2">
                    <span>View All Service Areas</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-border bg-background p-6 shadow-md sm:p-8">
                <div className="flex items-center gap-3 border-b border-border pb-4">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-secondary text-primary">
                    <MapPin className="size-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-default text-base font-bold text-foreground">
                      Primary Service Zones
                    </h3>
                    <p className="text-xs text-muted-foreground">Clayton County &amp; South Metro Atlanta</p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  {coveredZones.map((zone, i) => <div key={zone} data-index={i} className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2.5 text-xs font-semibold text-foreground">
                      <CheckCircle2 className="size-4 text-primary shrink-0" />
                      <span>{zone}, GA</span>
                    </div>)}
                </div>

                <div className="mt-5 rounded-lg bg-secondary/60 p-3 text-center text-xs text-muted-foreground">
                  Need service just outside these areas? Call <a href="tel:4708479076" className="font-bold text-primary underline">(470) 847-9076</a> for dispatch verification.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* (7) WHAT HAPPENS NEXT */}
      {/* ------------------------------------------------------------- */}
      <section id="process-steps" className="border-b border-border bg-background py-14 md:py-20">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
          <div className="text-center">
            <span className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
              Transparent Response
            </span>
            <h2 className="mt-2 font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl md:text-4xl">
              What Happens Next
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground sm:text-base">
              A straightforward three-step process from your first contact to complete resolution.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="relative rounded-2xl border border-border bg-card p-6 shadow-xs sm:p-8">
              <div className="flex size-12 items-center justify-center rounded-full bg-primary font-default text-lg font-bold text-primary-foreground">
                1
              </div>
              <h3 className="mt-5 font-default text-xl font-bold text-foreground">
                Submit Your Request
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                Call our direct phone number or submit the online form with your property location and a brief description of the plumbing symptom.
              </p>
            </div>

            <div className="relative rounded-2xl border border-border bg-card p-6 shadow-xs sm:p-8">
              <div className="flex size-12 items-center justify-center rounded-full bg-primary font-default text-lg font-bold text-primary-foreground">
                2
              </div>
              <h3 className="mt-5 font-default text-xl font-bold text-foreground">
                We Review the Problem
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                Our local dispatch team assesses the urgency, assigns the nearest available plumbing truck, and confirms your arrival window.
              </p>
            </div>

            <div className="relative rounded-2xl border border-border bg-card p-6 shadow-xs sm:p-8">
              <div className="flex size-12 items-center justify-center rounded-full bg-accent font-default text-lg font-bold text-accent-foreground">
                3
              </div>
              <h3 className="mt-5 font-default text-xl font-bold text-foreground">
                Schedule Service &amp; Repair
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                A licensed technician inspects the system on-site, provides a clear upfront quote, and begins the necessary repairs immediately.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* (8) FAQ ACCORDION */}
      {/* ------------------------------------------------------------- */}
      <section id="contact-faq" className="border-b border-border bg-card/40 py-14 md:py-20">
        <div className="mx-auto max-w-[900px] px-5 sm:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3.5 py-1 text-xs font-bold tracking-wide uppercase text-secondary-foreground">
              <HelpCircle className="size-3.5 text-primary" />
              Frequently Asked Questions
            </div>
            <h2 className="mt-3 font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl md:text-4xl">
              Contact FAQ
            </h2>
            <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground sm:text-base">
              Common questions regarding emergency response, scheduling, and service guidelines.
            </p>
          </div>

          <div className="mt-10 space-y-3.5">
            {faqItems.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return <div key={faq.question} data-index={idx} className="overflow-hidden rounded-xl border border-border bg-background shadow-xs transition-[border-color] hover:border-primary/40">
                  <Button type="button" variant="ghost" onClick={() => toggleFaq(idx)} aria-expanded={isOpen} className="h-auto w-full justify-between rounded-none px-5 py-4 text-left font-default text-base font-bold text-foreground hover:bg-transparent hover:text-primary sm:px-6">
                    <span>{faq.question}</span>
                    <ChevronDown className={`size-5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180 text-primary" : ""}`} />
                  </Button>

                  <AnimatePresence initial={false}>
                    {isOpen && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: "auto"
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25,
                  ease: "easeInOut"
                }}>
                        <div className="border-t border-border/60 px-5 pt-3 pb-5 text-sm leading-relaxed text-muted-foreground sm:px-6">
                          <p>{faq.answer}</p>
                          {faq.linkHref && faq.linkText && <div className="mt-3">
                              <Link to={faq.linkHref} className="inline-flex items-center gap-1 font-semibold text-primary underline underline-offset-2 hover:text-primary/80">
                                {faq.linkText} <ArrowRight className="size-3.5" />
                              </Link>
                            </div>}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* (9) FINAL CTA */}
      {/* ------------------------------------------------------------- */}
      <section id="final-contact-cta" className="bg-primary py-14 text-primary-foreground md:py-20">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 px-4 py-1.5 text-xs font-bold tracking-wider uppercase text-accent">
            <Clock className="size-3.5" />
            24/7 Jonesboro Direct Assistance
          </div>

          <h2 className="mt-4 font-default text-3xl font-extrabold tracking-tight text-primary-foreground sm:text-4xl md:text-5xl">
            Need a Plumber in Jonesboro?
          </h2>

          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-primary-foreground/80 sm:text-base">
            Whether you are facing an urgent burst pipe in the middle of the night or need a dependable plumbing fixture installation, our local team is ready to dispatch.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="lg" className="rounded-full bg-accent px-8 py-6 text-base font-bold text-accent-foreground shadow-lg transition-[background-color,box-shadow] hover:bg-accent/90">
              <a href="tel:4708479076" className="flex items-center gap-2.5">
                <PhoneCall className="size-5 shrink-0" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>

            <Button asChild variant="outline" size="lg" className="rounded-full border-primary-foreground/20 bg-primary-foreground px-8 py-6 text-base font-bold text-primary shadow-md transition-[background-color,color] hover:bg-primary-foreground/90 hover:text-primary">
              <Link to="#request-service-form" className="flex items-center gap-2">
                <span>Request Service</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <p className="mt-4 text-xs text-primary-foreground/70">
            Prompt on-site response throughout Jonesboro, GA &amp; Clayton County
          </p>
        </div>
      </section>
    </div>;
}